/**
 * Helpers for the publication version number.
 *
 * The backend sends `pubVersion` as a string, and it can reach the UI with a
 * prefix such as `v11`. Adding 1 to a string concatenates (`"11" + 1` is
 * `"111"`), so every version is parsed to an integer before any math or before
 * it is sent back.
 */

/**
 * Parses a version such as `11`, `"11"`, `"v11"` or `"V11"` into an integer.
 *
 * @param version the raw version value
 * @returns the integer version, or null when the value has no digits
 */
export const parseVersion = (version: unknown): number | null => {
    if (version == null) {
        return null;
    }
    const digitsOnly = String(version).replace(/\D/g, '');
    return digitsOnly === '' ? null : Number.parseInt(digitsOnly, 10);
};

/**
 * Returns the version number that follows the given one, e.g. `"v11"` -> `12`.
 * A missing or non-numeric version is treated as 0, so the next version is 1.
 *
 * @param version the current version value
 * @returns the next integer version
 */
export const withVersion = (title: string | undefined, version: unknown): string => {
    const parsed = parseVersion(version);
    return parsed && parsed > 0 ? `${title} (v${parsed})` : `${title}`;
};

export const getNextVersion = (version: unknown): number => (parseVersion(version) ?? 0) + 1;
